import { collection, getDocs, writeBatch, query, where } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

/**
 * Admin maintenance: Purge ALL documents from specified collections.
 * WARNING: This is a destructive operation that cannot be undone.
 * Use only for testing/development environments or when you need to completely reset data.
 */
export const purgeAllCollections = async ({ collections = [] }) => {
  if (!db) throw new Error('Database not initialized');
  if (!Array.isArray(collections) || collections.length === 0) {
    throw new Error('No collections specified for purge');
  }

  const results = {};
  
  for (const collectionName of collections) {
    try {
      const ref = collection(db, collectionName);
      const snap = await getDocs(ref);
      
      if (snap.empty) {
        results[collectionName] = 0;
        continue;
      }

      let count = 0;
      const docs = snap.docs;
      
      // Chunk deletes to respect Firestore batch limit (500)
      const chunkSize = 450;
      for (let i = 0; i < docs.length; i += chunkSize) {
        const batch = writeBatch(db);
        const slice = docs.slice(i, i + chunkSize);
        slice.forEach(d => batch.delete(d.ref));
        await batch.commit();
        count += slice.length;
      }
      
      results[collectionName] = count;
    } catch (error) {
      results[collectionName] = { error: error?.message || 'Unknown error' };
    }
  }
  
  return results;
};

/**
 * Count documents in multiple collections.
 */
export const countCollectionDocuments = async ({ collections = [], eventId = null }) => {
  if (!db) throw new Error('Database not initialized');
  if (!Array.isArray(collections) || collections.length === 0) {
    throw new Error('No collections specified');
  }

  const counts = {};
  
  for (const collectionName of collections) {
    try {
      const ref = collection(db, collectionName);
      const snap = eventId
        ? await getDocs(query(ref, where('eventId', '==', eventId)))
        : await getDocs(ref);
      counts[collectionName] = snap.size || 0;
    } catch (error) {
      counts[collectionName] = { error: error?.message || 'Unknown error' };
    }
  }
  
  return counts;
};

/**
 * Purge documents from specified collections that belong to a particular event.
 * Only documents with a top-level `eventId` field matching the provided eventId
 * will be deleted. Collections without an `eventId` field will have 0 deletions.
 *
 * @param {Object} params
 * @param {Array<string>} params.collections - list of collection names to purge
 * @param {string} params.eventId - event id to scope deletions to
 * @returns {Promise<Object>} - map of collectionName => deletedCount or { error }
 */
export const purgeCollectionsForEvent = async ({ collections = [], eventId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!Array.isArray(collections) || collections.length === 0) {
    throw new Error('No collections specified for purge');
  }
  if (!eventId) throw new Error('eventId is required for event-scoped purge');

  const results = {};

  for (const collectionName of collections) {
    try {
      const ref = collection(db, collectionName);
      // Query for docs matching eventId; if collection doesn't have eventId field,
      // this will simply return zero documents.
      const q = query(ref, where('eventId', '==', eventId));
      const snap = await getDocs(q);

      if (snap.empty) {
        results[collectionName] = 0;
        continue;
      }

      let count = 0;
      const docs = snap.docs;
      const chunkSize = 450;
      for (let i = 0; i < docs.length; i += chunkSize) {
        const batch = writeBatch(db);
        const slice = docs.slice(i, i + chunkSize);
        slice.forEach(d => batch.delete(d.ref));
        await batch.commit();
        count += slice.length;
      }

      results[collectionName] = count;
    } catch (error) {
      results[collectionName] = { error: error?.message || 'Unknown error' };
    }
  }

  return results;
};
